import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

/**
 * Reminders — master-detail editor for the user's own reminder checklist.
 *
 * Master panel (left):  filters + list with status icon (green/amber/red/grey).
 * Detail panel (right): full edit form for the selected reminder.
 *
 * Each reminder is owner-scoped: only the user who created it sees it here.
 * Assignees ("people responsible") get notified by the scheduler when due.
 */

const CATEGORIES = ['Internal', 'External', 'Statutory'];
const CYCLES = ['Yearly', 'Half-Yearly', 'Quarterly', 'Monthly', 'Weekly'];
const FREQUENCIES = ['Daily', 'Twice Daily', 'Weekly', 'Monthly', 'Custom'];
const REPEAT_TILL = ['Not required', 'Till Confirmation'];
const MONTHS = [
  { v: 1, l: 'Jan' }, { v: 2, l: 'Feb' }, { v: 3, l: 'Mar' }, { v: 4, l: 'Apr' },
  { v: 5, l: 'May' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Aug' },
  { v: 9, l: 'Sep' }, { v: 10, l: 'Oct' }, { v: 11, l: 'Nov' }, { v: 12, l: 'Dec' },
];
const WEEKDAYS = [
  { v: 1, l: 'Mon' }, { v: 2, l: 'Tue' }, { v: 3, l: 'Wed' }, { v: 4, l: 'Thu' },
  { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' }, { v: 7, l: 'Sun' },
];

const EMPTY = {
  id: null,
  name: '',
  message: '',
  category: 'Internal',
  cycle: 'Monthly',
  due_day: 1,
  due_months: [],
  start_before_days: 5,
  reminder_times: ['09:00'],
  frequency: 'Daily',
  confirmation_required: false,
  repeat_till: 'Not required',
  status: 'Active',
  assignees: [],
  custom_days: [],
  calendar_id: '',
  tasks: [],                                // chain of sub-tasks (optional)
  entity_type: '',                          // optional link to an app object
  entity_id: '',
  entity_label: '',
};
const ENTITY_TYPES = [
  { value: '', label: '— None —' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'lead', label: 'Lead' },
  { value: 'customer', label: 'Customer' },
  { value: 'contract', label: 'Contract' },
  { value: 'project', label: 'Project' },
  { value: 'estimate', label: 'Estimate' },
];
const ENTITY_API = {
  opportunity: { url: '/api/crm/opportunities?limit=300', key: 'opportunities', label: (o) => o.title || o.name || o.id },
  lead:        { url: '/api/crm/leads?limit=300',        key: 'leads',         label: (o) => o.title || o.contact_name || o.id },
  customer:    { url: '/api/customers?limit=300',        key: 'customers',     label: (o) => o.name || o.id },
  contract:    { url: '/api/contracts?limit=300',        key: 'contracts',     label: (o) => o.contract_number ? `${o.contract_number} — ${o.title || ''}` : (o.title || o.id) },
  project:     { url: '/api/projects?limit=300',         key: 'projects',      label: (o) => o.name || o.project_code || o.id },
  estimate:    { url: '/api/estimates?limit=300',        key: 'effortEstimates', label: (o) => o.name || o.id },
};

const ICON_COLORS = {
  green:   { bg: 'rgba(16,185,129,0.18)',  fg: '#10B981', label: 'Confirmed' },
  amber:   { bg: 'rgba(245,158,11,0.20)',  fg: '#F59E0B', label: 'Awaiting confirmation' },
  red:     { bg: 'rgba(239,68,68,0.18)',   fg: '#EF4444', label: 'Missed' },
  neutral: { bg: 'rgba(148,163,184,0.18)', fg: '#94A3B8', label: 'No runs yet' },
};

function StatusDot({ icon }) {
  const c = ICON_COLORS[icon] || ICON_COLORS.neutral;
  return (
    <span
      title={c.label}
      style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
        background: c.fg, boxShadow: `0 0 0 3px ${c.bg}`,
      }}
    />
  );
}

export default function Reminders() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  // selectedId can be a UUID (existing row), null (initial / nothing chosen),
  // or the string '_new' (user is creating a brand-new reminder — auto-pick
  // and the form-load effect both ignore this sentinel).
  const [selectedId, setSelectedId] = useState(null);
  const [hasAutoPicked, setHasAutoPicked] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ search: '', category: '', status: '', icon: '', employee: '' });
  // editing=false means the detail panel shows the reminder as read-only
  // labels with an Edit button. editing=true switches it to the form. New
  // reminders go straight to edit mode.
  const [editing, setEditing] = useState(false);
  // Show Schedule modal state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  // Calendars list (for the Calendar dropdown on the form)
  const [calendars, setCalendars] = useState([]);
  // Tab: 'mine' = reminders I OWN; 'inbox' = reminders assigned to ME
  const [tab, setTab] = useState('mine');
  const [inbox, setInbox] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);

  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    try {
      const r = await apiFetch('/api/reminders/assigned/inbox');
      const d = await r.json();
      if (r.ok) setInbox(d.items || []);
    } catch (_) { /* ignore */ }
    setInboxLoading(false);
  }, []);
  useEffect(() => { if (tab === 'inbox') loadInbox(); }, [tab, loadInbox]);

  // ── Load list + employees ────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.status) params.set('status', filters.status);
      if (filters.icon) params.set('icon', filters.icon);
      if (filters.employee) params.set('employee', filters.employee);
      const [lr, er, cr] = await Promise.all([
        apiFetch('/api/reminders?' + params.toString()),
        apiFetch('/api/employees?limit=500'),
        apiFetch('/api/metadata/lookups?category=calendar'),
      ]);
      if (lr.ok) {
        const d = await lr.json();
        setList(d.reminders || []);
      }
      if (er.ok) {
        const d = await er.json();
        setEmployees(d.employees || []);
      }
      if (cr && cr.ok) {
        const d = await cr.json();
        setCalendars((d.rows || d.lookups || []).filter((r) => r.status !== 'Inactive'));
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  // Auto-pick the first row only ONCE, on initial load — never again.
  // Otherwise clicking "+ New Reminder" (which sets selectedId='_new') would
  // be stomped by an auto-pick that snaps back to list[0].
  useEffect(() => {
    if (!hasAutoPicked && !selectedId && list.length > 0) {
      setSelectedId(list[0].id);
      setHasAutoPicked(true);
    }
  }, [list, selectedId, hasAutoPicked]);

  // Load detail into form whenever selection changes — but skip the '_new'
  // sentinel (newReminder() has already populated the form correctly).
  useEffect(() => {
    if (!selectedId || selectedId === '_new') return;
    const local = list.find((r) => r.id === selectedId);
    if (local) {
      setForm({
        id: local.id,
        name: local.name || '',
        message: local.message || '',
        category: local.category || 'Internal',
        cycle: local.cycle || 'Monthly',
        due_day: local.due_day || 1,
        due_months: Array.isArray(local.due_months) ? local.due_months : [],
        start_before_days: local.start_before_days || 0,
        reminder_times: Array.isArray(local.reminder_times) ? local.reminder_times : [],
        frequency: local.frequency || 'Daily',
        confirmation_required: !!local.confirmation_required,
        repeat_till: local.repeat_till || 'Not required',
        status: local.status || 'Active',
        assignees: (local.assignees || []).map((a) => a.employee_id),
        custom_days: Array.isArray(local.custom_days) ? local.custom_days : [],
        calendar_id: local.calendar_id || '',
        tasks: [],                          // populated by the tasks fetch below
        entity_type: local.entity_type || '',
        entity_id: local.entity_id || '',
        entity_label: local.entity_label || '',
      });
      setDirty(false);
      setEditing(false);                    // open in read-only view
      // Fetch this reminder's task chain — separate endpoint so we don't bloat
      // the list payload when most reminders won't have tasks.
      apiFetch(`/api/reminders/${local.id}/tasks`).then(async (r) => {
        if (!r.ok) return;
        const d = await r.json();
        setForm((p) => ({ ...p, tasks: d.tasks || [] }));
      }).catch(() => {});
    }
  }, [selectedId, list]);

  function setField(k, v) { setForm((p) => ({ ...p, [k]: v })); setDirty(true); }

  // ── New / Save / Delete / Pause / Resume ─────────────────────────────
  function newReminder() {
    // Use '_new' sentinel rather than null — null retriggers the auto-pick
    // effect, which would overwrite the freshly-created form with list[0].
    setSelectedId('_new');
    setForm({ ...EMPTY, name: 'New reminder' });
    setDirty(true);
    setEditing(true);                     // new records open in edit mode
  }

  async function save() {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const body = JSON.stringify({
        name: form.name,
        message: form.message,
        category: form.category,
        cycle: form.cycle,
        due_day: parseInt(form.due_day, 10) || 1,
        due_months: form.due_months,
        start_before_days: parseInt(form.start_before_days, 10) || 0,
        reminder_times: form.reminder_times,
        frequency: form.frequency,
        confirmation_required: !!form.confirmation_required,
        repeat_till: form.repeat_till,
        status: form.status,
        assignees: form.assignees,
        custom_days: form.custom_days || [],
        calendar_id: form.calendar_id || null,
      });
      const url = form.id ? `/api/reminders/${form.id}` : '/api/reminders';
      const method = form.id ? 'PUT' : 'POST';
      const r = await apiFetch(url, { method, body });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Save failed');
      const nextId = form.id || d.id;
      // Sync the task chain on the same save action (only if we're editing
      // an existing reminder OR the new reminder has tasks defined).
      if ((form.tasks || []).length > 0 || form.id) {
        const tr = await apiFetch(`/api/reminders/${nextId}/tasks`, {
          method: 'PUT',
          body: JSON.stringify({ tasks: form.tasks || [] }),
        });
        if (!tr.ok) {
          const td = await tr.json().catch(() => ({}));
          toast.error('Saved reminder but task chain failed: ' + (td.error || ''));
        }
      }
      toast.success(form.id ? 'Reminder updated' : 'Reminder created');
      setDirty(false);
      setEditing(false);                    // back to read-only after save
      await load();
      setSelectedId(nextId);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteReminder() {
    if (!form.id) return;
    const r = list.find((x) => x.id === form.id) || {};
    const ok = await toast.confirm({
      title: 'Delete reminder?',
      body: `Delete "${r.name || 'this reminder'}"? This removes its ${(r.assignees || []).length} assignee(s) and all run history. Cannot be undone.`,
      danger: true, confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`/api/reminders/${form.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Delete failed'); }
      toast.success('Deleted');
      setSelectedId(null);
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function togglePause() {
    if (!form.id) return;
    const url = form.status === 'Paused'
      ? `/api/reminders/${form.id}/resume`
      : `/api/reminders/${form.id}/pause`;
    try {
      const res = await apiFetch(url, { method: 'POST' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      toast.success(form.status === 'Paused' ? 'Resumed' : 'Paused');
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function testFire() {
    if (!form.id) return;
    try {
      const res = await apiFetch(`/api/reminders/_test-fire/${form.id}`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Test fire failed');
      toast.success(`Fired — ${d.notificationCount || 0} notifications, ${d.emailCount || 0} emails`);
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function showSchedule() {
    if (!form.id) {
      toast.error('Save the reminder first to preview the schedule.');
      return;
    }
    setScheduleOpen(true);
    setScheduleData(null);
    try {
      const res = await apiFetch(`/api/reminders/${form.id}/schedule?count=10`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Schedule fetch failed');
      setScheduleData(d.occurrences || []);
    } catch (e) {
      toast.error(e.message);
      setScheduleOpen(false);
    }
  }

  // ── Helpers for sub-controls ─────────────────────────────────────────
  function toggleArrayValue(key, v) {
    const arr = form[key] || [];
    const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    setField(key, next);
  }
  function addTime() {
    const t = '09:00';
    setField('reminder_times', [...(form.reminder_times || []), t]);
  }
  function setTime(idx, v) {
    const arr = [...(form.reminder_times || [])];
    arr[idx] = v;
    setField('reminder_times', arr);
  }
  function removeTime(idx) {
    const arr = (form.reminder_times || []).filter((_, i) => i !== idx);
    setField('reminder_times', arr);
  }

  const empOptions = useMemo(
    () => employees.map((e) => ({
      id: e.id,
      label: `${e.first_name || ''} ${e.last_name || ''}`.trim() + (e.emp_code ? ` (${e.emp_code})` : ''),
    })),
    [employees]
  );

  const isWeekly = form.cycle === 'Weekly';

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header title removed — the page title is already shown in the topbar
          via the route registration in bizsuite-app.jsx. The "+ New Reminder"
          button moves into the master-panel header below so it sits next to
          the filters where it logically belongs. */}

      {/* Tabs: My Reminders (owner) / Inbox (assigned-to-me with pending acks) */}
      <div style={S.tabs}>
        <button onClick={() => setTab('mine')}
                style={{ ...S.tab, ...(tab === 'mine' ? S.tabActive : {}) }}>My Reminders</button>
        <button onClick={() => setTab('inbox')}
                style={{ ...S.tab, ...(tab === 'inbox' ? S.tabActive : {}) }}>
          Inbox
          {inbox.filter((x) => !x.my_confirmed).length > 0 && (
            <span style={S.tabBadge}>{inbox.filter((x) => !x.my_confirmed).length}</span>
          )}
        </button>
      </div>

      {tab === 'inbox' ? (
        <InboxView items={inbox} loading={inboxLoading} onConfirmed={loadInbox} />
      ) : (
      <div style={S.split}>
        {/* ─── Master ─── */}
        <div style={S.master}>
          <div style={S.masterHeader}>
            <button style={S.cyanBtn} onClick={newReminder}>+ New Reminder</button>
            <input
              type="text" placeholder="Search name or message…"
              style={S.searchInput} value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            />
            <div style={S.filterRow}>
              <SearchableSelect
                value={filters.category}
                onChange={(v) => setFilters((p) => ({ ...p, category: v || '' }))}
                options={[{ value: '', label: 'All categories' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]}
                placeholder="All categories" sort={false}
              />
              <SearchableSelect
                value={filters.icon}
                onChange={(v) => setFilters((p) => ({ ...p, icon: v || '' }))}
                options={[
                  { value: '', label: 'All status' },
                  { value: 'green', label: 'Confirmed' },
                  { value: 'amber', label: 'Awaiting' },
                  { value: 'red', label: 'Missed' },
                  { value: 'neutral', label: 'No runs' },
                ]}
                placeholder="All status" sort={false}
              />
            </div>
            <div style={S.filterRow}>
              <SearchableSelect
                value={filters.status}
                onChange={(v) => setFilters((p) => ({ ...p, status: v || '' }))}
                options={[
                  { value: '', label: 'Active & Paused' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Paused', label: 'Paused' },
                ]}
                placeholder="Active & Paused" sort={false}
              />
              <SearchableSelect
                value={filters.employee}
                onChange={(v) => setFilters((p) => ({ ...p, employee: v || '' }))}
                options={[{ value: '', label: 'Any person' }, ...empOptions.map((e) => ({ value: e.id, label: e.label }))]}
                placeholder="Any person"
              />
            </div>
          </div>

          <div style={S.masterList}>
            {loading ? (
              <div style={S.empty}>Loading…</div>
            ) : list.length === 0 ? (
              <div style={S.empty}>No reminders match. Click <strong>New Reminder</strong> to create one.</div>
            ) : (
              list.map((r) => {
                const isSel = r.id === selectedId;
                return (
                  <div key={r.id}
                       onClick={() => setSelectedId(r.id)}
                       style={{ ...S.row, ...(isSel ? S.rowSel : {}) }}>
                    <StatusDot icon={r.status_icon} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...S.rowName, color: isSel ? '#00E0FF' : '#E2E8F0' }}>{r.name}</div>
                      <div style={S.rowSub}>
                        {r.category} · {r.cycle} · day {r.due_day}
                        {r.status === 'Paused' && <span style={S.pillPaused}>Paused</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ─── Detail ─── */}
        <div style={S.detail}>
          {!form.id && !dirty ? (
            <div style={{ ...S.empty, padding: 32 }}>
              Select a reminder on the left, or click <strong>+ New Reminder</strong> to create one.
            </div>
          ) : (
            <>
              <div style={S.detailHeader}>
                {editing ? (
                  <input
                    type="text" style={S.titleInput}
                    value={form.name} onChange={(e) => setField('name', e.target.value)}
                    placeholder="Reminder name"
                  />
                ) : (
                  <div style={S.titleReadOnly}>{form.name || '(no name)'}</div>
                )}
                <div style={S.detailActions}>
                  {form.id && !editing && (
                    <>
                      <button style={S.ghostBtn} onClick={() => setEditing(true)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></svg>
                          Edit
                        </span>
                      </button>
                      <button style={S.ghostBtn} onClick={showSchedule}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                          Show Schedule
                        </span>
                      </button>
                      <button style={S.ghostBtn} onClick={togglePause} title={form.status === 'Paused' ? 'Resume' : 'Pause'}>
                        {form.status === 'Paused' ? '▶ Resume' : '⏸ Pause'}
                      </button>
                      <button style={S.ghostBtn} onClick={testFire} title="Fire one notification + email now to test the pipeline">
                        Test fire
                      </button>
                      <button style={{ ...S.ghostBtn, color: '#FCA5A5', borderColor: 'rgba(239,68,68,0.4)' }} onClick={deleteReminder}>
                        Delete
                      </button>
                    </>
                  )}
                  {editing && (
                    <>
                      {form.id && (
                        <button style={S.ghostBtn} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                      )}
                      <button style={S.primaryBtn} onClick={save} disabled={saving || !dirty}>
                        {saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create reminder')}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {editing ? (
                <div style={S.formGrid}>
                  <Field label="Category">
                    <SearchableSelect value={form.category} onChange={(v) => setField('category', v)}
                      options={CATEGORIES.map((c) => ({ value: c, label: c }))} sort={false} />
                  </Field>

                  <Field label="Cycle">
                    <SearchableSelect value={form.cycle}
                      onChange={(v) => { setField('cycle', v); setField('due_months', []); }}
                      options={CYCLES.map((c) => ({ value: c, label: c }))} sort={false} />
                  </Field>

                  <Field label={isWeekly ? 'Day of week' : 'Day of month (1–31)'}>
                    {isWeekly ? (
                      <SearchableSelect value={form.due_day}
                        onChange={(v) => setField('due_day', parseInt(v, 10))}
                        options={WEEKDAYS.map((w) => ({ value: w.v, label: w.l }))} sort={false} />
                    ) : (
                      <input type="number" min="1" max="31" style={S.input}
                             value={form.due_day} onChange={(e) => setField('due_day', parseInt(e.target.value, 10) || 1)} />
                    )}
                  </Field>

                  <Field label="Start before due (days)">
                    <input type="number" min="0" max="60" style={S.input}
                           value={form.start_before_days}
                           onChange={(e) => setField('start_before_days', parseInt(e.target.value, 10) || 0)} />
                  </Field>

                  <Field label="Frequency">
                    <SearchableSelect value={form.frequency} onChange={(v) => setField('frequency', v)}
                      options={FREQUENCIES.map((f) => ({ value: f, label: f }))} sort={false} />
                  </Field>

                  <Field label="Repeat reminder">
                    <SearchableSelect value={form.repeat_till} onChange={(v) => setField('repeat_till', v)}
                      options={REPEAT_TILL.map((r) => ({ value: r, label: r }))} sort={false} />
                  </Field>

                  <Field label="Calendar (drives weekly off / preponement)" full>
                    <SearchableSelect value={form.calendar_id || ''}
                      onChange={(v) => setField('calendar_id', v || '')}
                      options={[
                        { value: '', label: 'Default · Mon–Fri working' },
                        ...calendars.map((c) => ({ value: c.id, label: c.name })),
                      ]}
                      placeholder="Default · Mon–Fri working" />
                  </Field>

                  {form.frequency === 'Custom' && (
                    <Field label="Days of week (Custom)" full>
                      <div style={S.chipRow}>
                        {WEEKDAYS.map((w) => {
                          const on = (form.custom_days || []).includes(w.v);
                          return (
                            <button key={w.v} type="button"
                                    onClick={() => toggleArrayValue('custom_days', w.v)}
                                    style={{ ...S.chip, ...(on ? S.chipOn : {}) }}>
                              {w.l}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                        On a holiday or weekly off, the run is preponed to the previous working day automatically.
                      </div>
                    </Field>
                  )}

                  <Field label="Confirmation required" full>
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#CBD5E1' }}>
                      <input type="checkbox" checked={form.confirmation_required}
                             onChange={(e) => setField('confirmation_required', e.target.checked)} />
                      Assignee must acknowledge each fire
                    </label>
                  </Field>

                  {!isWeekly && (
                    <Field label="Due months (multi)" full>
                      <div style={S.chipRow}>
                        {MONTHS.map((m) => {
                          const on = (form.due_months || []).includes(m.v);
                          return (
                            <button key={m.v} type="button"
                                    onClick={() => toggleArrayValue('due_months', m.v)}
                                    style={{ ...S.chip, ...(on ? S.chipOn : {}) }}>
                              {m.l}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}

                  <Field label="Time of reminder" full>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {(form.reminder_times || []).map((t, i) => (
                        <div key={i} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <input type="time" style={{ ...S.input, width: 100 }}
                                 value={t} onChange={(e) => setTime(i, e.target.value)} />
                          <button style={S.miniDel} onClick={() => removeTime(i)} title="Remove time">×</button>
                        </div>
                      ))}
                      <button style={S.addBtn} onClick={addTime} type="button">+ Add time</button>
                    </div>
                  </Field>

                  <Field label="People responsible (multi)" full>
                    <MultiPersonPicker
                      value={form.assignees}
                      options={empOptions}
                      onChange={(arr) => setField('assignees', arr)}
                    />
                  </Field>

                  <Field label="Message (sent in email + notification)" full>
                    <textarea rows={4} style={{ ...S.input, fontFamily: 'inherit', resize: 'vertical' }}
                              value={form.message} onChange={(e) => setField('message', e.target.value)}
                              placeholder="What should the reminder text say? Include any links or instructions." />
                  </Field>

                  {/* Object link — pick a record from the app (Opportunity,
                      Lead, Contract, etc.) so this reminder is a follow-up
                      tied to that record. The notification body deep-links
                      back to the entity's editor page. */}
                  <Field label="Linked object (optional)" full>
                    <ObjectLinkPicker
                      type={form.entity_type}
                      id={form.entity_id}
                      label={form.entity_label}
                      onChange={(t, i, l) => {
                        setField('entity_type', t || '');
                        setField('entity_id', i || '');
                        setField('entity_label', l || '');
                      }}
                    />
                  </Field>

                  {/* Tasks chain — optional ordered list of sub-tasks. When
                      tasks are defined, the scheduler fires them sequentially:
                      each task waits for the previous to be confirmed. */}
                  <Field label="Tasks (chain — optional)" full>
                    <TaskChainEditor
                      value={form.tasks || []}
                      onChange={(arr) => setField('tasks', arr)}
                      assigneeIds={form.assignees || []}
                      empOptions={empOptions}
                    />
                  </Field>
                </div>
              ) : (
                <ReadOnlyView form={form} empOptions={empOptions} calendars={calendars} />
              )}
            </>
          )}
        </div>
      </div>

      )}

      {/* ─── Show Schedule modal ─── */}
      {scheduleOpen && (
        <ScheduleModal
          data={scheduleData}
          onClose={() => setScheduleOpen(false)}
          reminderName={form.name}
          assignees={(form.assignees || [])
            .map((id) => (empOptions.find((e) => e.id === id) || {}).label)
            .filter(Boolean)}
        />
      )}
    </div>
  );
}

/**
 * InboxView — the assignee-side surface. Lists every fired-but-unconfirmed
 * reminder/task that targets this user, with a Confirm button per row. Once
 * confirmed, the row turns green-checked and (for task chains) the next task
 * fires on the next scheduler tick.
 */
function InboxView({ items = [], loading, onConfirmed }) {
  const [busyId, setBusyId] = useState(null);

  async function confirm(item) {
    setBusyId(item.run_id);
    try {
      // Task fires use the task-confirm endpoint so the next task in seq
      // unlocks; whole-reminder fires use the run-confirm endpoint.
      const url = item.task_id
        ? `/api/reminders/tasks/${item.task_id}/confirm`
        : `/api/reminders/runs/${item.run_id}/confirm`;
      const r = await apiFetch(url, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Confirm failed');
      toast.success('Confirmed');
      if (onConfirmed) onConfirmed();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const pending = items.filter((x) => !x.my_confirmed);
  const recent = items.filter((x) => x.my_confirmed).slice(0, 20);

  return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 14 }}>
      <div style={{ marginBottom: 10, fontSize: 12, color: '#94A3B8' }}>
        {loading ? 'Loading…' : `${pending.length} pending · ${recent.length} recently confirmed`}
      </div>

      {!loading && pending.length === 0 && recent.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
          You have nothing in your reminder inbox right now. When a reminder fires for you, it'll appear here.
        </div>
      )}

      {pending.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Pending acknowledgement</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {pending.map((it) => (
              <InboxRow key={it.run_id} item={it} onConfirm={confirm} busy={busyId === it.run_id} pending />
            ))}
          </div>
        </>
      )}

      {recent.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Recently confirmed</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recent.map((it) => (
              <InboxRow key={it.run_id} item={it} onConfirm={() => {}} busy={false} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InboxRow({ item, onConfirm, busy, pending }) {
  const fired = new Date(item.fired_at);
  const ago = (() => {
    const ms = Date.now() - fired.getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  })();
  const title = item.task_description
    ? `${item.reminder_name} — ${item.task_description}`
    : item.reminder_name;
  const body = item.task_message || item.reminder_message;

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
      padding: '10px 12px', borderRadius: 8,
      background: pending ? 'rgba(245,158,11,0.05)' : 'rgba(16,185,129,0.05)',
      border: `1px solid ${pending ? 'rgba(245,158,11,0.20)' : 'rgba(16,185,129,0.18)'}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>
          {pending ? null : <span style={{ color: '#10B981', marginRight: 6 }}>✓</span>}
          {title}
          {item.task_seq != null && <span style={{ fontSize: 10.5, color: '#94A3B8', marginLeft: 6 }}>step {item.task_seq}</span>}
        </div>
        {body && <div style={{ fontSize: 12, color: '#CBD5E1', marginTop: 2 }}>{body}</div>}
        <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>Fired {ago}</span>
          {item.entity_type && item.entity_id && (
            <span style={{ color: '#67E8F9' }}>↗ {item.entity_type}: {item.entity_label || item.entity_id}</span>
          )}
        </div>
      </div>
      <div>
        {pending ? (
          <button onClick={() => onConfirm(item)} disabled={busy}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 600, color: '#fff',
              background: 'linear-gradient(135deg, #10B981, #3B8FF4)',
              border: 'none', borderRadius: 6, cursor: 'pointer', opacity: busy ? 0.6 : 1,
            }}>
            {busy ? 'Confirming…' : '✓ Confirm'}
          </button>
        ) : (
          <span style={{ fontSize: 11, color: '#10B981' }}>Confirmed</span>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only display of a reminder. Mirrors the form layout but renders each
 * field as a label/value pair. The user clicks Edit (in the panel header)
 * to switch into the editable form view.
 */
function ReadOnlyView({ form, empOptions, calendars }) {
  const monthNames = MONTHS.filter((m) => (form.due_months || []).includes(m.v)).map((m) => m.l).join(', ') || '—';
  const weekdayNames = WEEKDAYS.filter((w) => (form.custom_days || []).includes(w.v)).map((w) => w.l).join(', ') || '—';
  const calName = (calendars.find((c) => c.id === form.calendar_id) || {}).name || 'Default · Mon–Fri working';
  const assigneeNames = (form.assignees || [])
    .map((id) => (empOptions.find((e) => e.id === id) || {}).label)
    .filter(Boolean).join(', ') || '—';
  const isWeekly = form.cycle === 'Weekly';

  function row(label, value) {
    return (
      <div>
        <div style={S.fieldLabel}>{label}</div>
        <div style={S.viewValue}>{value || '—'}</div>
      </div>
    );
  }

  return (
    <div style={S.formGrid}>
      {row('Category', form.category)}
      {row('Cycle', form.cycle)}
      {row(isWeekly ? 'Day of week' : 'Day of month', isWeekly ? (WEEKDAYS.find((w) => w.v === form.due_day) || {}).l : form.due_day)}
      {row('Start before due (days)', form.start_before_days)}
      {row('Frequency', form.frequency)}
      {row('Repeat reminder', form.repeat_till)}
      <div style={{ gridColumn: '1 / -1' }}>{row('Calendar', calName)}</div>
      {form.frequency === 'Custom' && <div style={{ gridColumn: '1 / -1' }}>{row('Days of week', weekdayNames)}</div>}
      <div style={{ gridColumn: '1 / -1' }}>{row('Confirmation required', form.confirmation_required ? 'Yes — assignees must acknowledge' : 'No')}</div>
      {!isWeekly && <div style={{ gridColumn: '1 / -1' }}>{row('Due months', monthNames)}</div>}
      <div style={{ gridColumn: '1 / -1' }}>{row('Time of reminder', (form.reminder_times || []).join(', ') || '—')}</div>
      <div style={{ gridColumn: '1 / -1' }}>{row('People responsible', assigneeNames)}</div>
      <div style={{ gridColumn: '1 / -1' }}>{row('Message', form.message)}</div>
      {(form.entity_type && form.entity_id) && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={S.fieldLabel}>Linked object</div>
          <div style={S.viewValue}>
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: 999,
              background: 'rgba(0,224,255,0.10)', color: '#67E8F9',
              border: '1px solid rgba(0,224,255,0.35)', fontSize: 11, fontWeight: 600,
              marginRight: 6, textTransform: 'capitalize',
            }}>{form.entity_type}</span>
            {form.entity_label || form.entity_id}
          </div>
        </div>
      )}
      {(form.tasks || []).length > 0 && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={S.fieldLabel}>Tasks ({form.tasks.length})</div>
          <ol style={{ paddingLeft: 18, margin: 0, color: '#E2E8F0', fontSize: 13 }}>
            {form.tasks.map((t) => (
              <li key={t.id} style={{ padding: '3px 0' }}>
                <strong>{t.description}</strong>
                {t.hours_before_due ? <span style={{ color: '#94A3B8', fontSize: 11 }}> · {t.hours_before_due}h before due</span> : null}
                {t.person_name ? <span style={{ color: '#94A3B8', fontSize: 11 }}> · {t.person_name}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/**
 * Show Schedule modal — lists the next N fire dates with a small "preponed
 * from <orig> (reason)" annotation when the calendar/holiday rules have
 * pulled the run earlier.
 */
/**
 * TaskChainEditor — inline list of sub-tasks. Add/remove rows; each row has
 * Seq · Description · Message · Hours before due · Person · Confirmation.
 * Person dropdown is restricted to the parent reminder's assignee list (so
 * users can't pick someone who won't be notified anyway).
 */
function TaskChainEditor({ value = [], onChange, assigneeIds = [], empOptions = [] }) {
  const tasks = value;
  const personOpts = empOptions
    .filter((e) => assigneeIds.includes(e.id))
    .map((e) => ({ value: e.id, label: e.label }));
  function patch(idx, p) {
    onChange(tasks.map((t, i) => i === idx ? { ...t, ...p } : t));
  }
  function remove(idx) {
    onChange(tasks.filter((_, i) => i !== idx));
  }
  function add() {
    const nextSeq = (tasks.reduce((m, t) => Math.max(m, parseInt(t.seq, 10) || 0), 0) + 10);
    onChange([...tasks, {
      id: '_new_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      seq: nextSeq,
      description: '',
      message: '',
      hours_before_due: 0,
      person_responsible_id: '',
      confirmation_required: true,
    }]);
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {tasks.length === 0 && (
        <div style={{ fontSize: 12, color: '#64748B', padding: 8, border: '1px dashed rgba(255,255,255,0.06)', borderRadius: 6 }}>
          No tasks yet. Add one to break this reminder into a sequential chain (e.g. for GSTR1: <em>Finish invoicing</em> → <em>File GSTR1</em>).
        </div>
      )}
      {tasks.map((t, i) => (
        <div key={t.id || i} style={S.taskRow}>
          <input type="number" style={{ ...S.input, width: 50 }} value={t.seq ?? 0}
                 onChange={(e) => patch(i, { seq: parseInt(e.target.value, 10) || 0 })} title="Sequence" />
          <input type="text" style={{ ...S.input, flex: 1.5 }} value={t.description || ''}
                 onChange={(e) => patch(i, { description: e.target.value })} placeholder="Task description" />
          <input type="text" style={{ ...S.input, flex: 2 }} value={t.message || ''}
                 onChange={(e) => patch(i, { message: e.target.value })} placeholder="Message (optional)" />
          <input type="number" style={{ ...S.input, width: 60 }} value={t.hours_before_due ?? 0}
                 onChange={(e) => patch(i, { hours_before_due: parseInt(e.target.value, 10) || 0 })}
                 title="Hours before due date" />
          <div style={{ flex: 1, minWidth: 140 }}>
            <SearchableSelect value={t.person_responsible_id || ''}
              onChange={(v) => patch(i, { person_responsible_id: v || null })}
              options={[{ value: '', label: '— Person —' }, ...personOpts]}
              placeholder="Person" />
          </div>
          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11, color: '#CBD5E1' }} title="Confirmation required">
            <input type="checkbox" checked={t.confirmation_required !== false}
                   onChange={(e) => patch(i, { confirmation_required: e.target.checked })} />
            Conf
          </label>
          <button style={S.miniDel} onClick={() => remove(i)} title="Remove task">×</button>
        </div>
      ))}
      <button type="button" style={S.addBtn} onClick={add}>+ Add task</button>
    </div>
  );
}

/**
 * ObjectLinkPicker — pair of selects. First picks an entity type
 * (Opportunity/Lead/Customer/etc.); second loads that type's records and
 * lets the user pick one. The picked record's id + display label are
 * persisted on the reminder.
 */
function ObjectLinkPicker({ type, id, label, onChange }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!type) { setRecords([]); return; }
    const cfg = ENTITY_API[type];
    if (!cfg) return;
    setLoading(true);
    apiFetch(cfg.url).then(async (r) => {
      if (!r.ok) { setRecords([]); return; }
      const d = await r.json();
      const list = d[cfg.key] || d.data || [];
      setRecords(list.map((o) => ({ id: o.id, label: cfg.label(o) })));
    }).catch(() => setRecords([])).finally(() => setLoading(false));
  }, [type]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8 }}>
      <SearchableSelect
        value={type || ''}
        onChange={(v) => onChange(v || '', '', '')}
        options={ENTITY_TYPES}
        sort={false}
        placeholder="Object type"
      />
      <SearchableSelect
        value={id || ''}
        disabled={!type || loading}
        onChange={(v) => {
          const rec = records.find((r) => r.id === v);
          onChange(type, v || '', rec ? rec.label : '');
        }}
        options={!type ? [{ value: '', label: '— pick a type first —' }]
                       : (loading ? [{ value: '', label: 'Loading…' }]
                                  : [{ value: '', label: '— None —' }, ...records.map((r) => ({ value: r.id, label: r.label }))])}
        placeholder={!type ? 'Pick a type first' : (loading ? 'Loading…' : 'Pick a record')}
      />
    </div>
  );
}

function ScheduleModal({ data, onClose, reminderName, assignees = [] }) {
  return (
    <div style={S.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        <div style={S.modalHeader}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#E2E8F0' }}>
            Next 10 occurrences {reminderName ? `· ${reminderName}` : ''}
          </span>
          <button style={S.ghostBtn} onClick={onClose}>Close</button>
        </div>
        {/* Assignees who'll receive each fire — useful context when reviewing
            the schedule. Renders as compact cyan chips. */}
        {assignees.length > 0 && (
          <div style={{ padding: '10px 18px 0 18px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 6 }}>Notifies</span>
            {assignees.map((n) => (
              <span key={n} style={S.chipPerson}>{n}</span>
            ))}
          </div>
        )}
        <div style={S.modalBody}>
          {data === null ? (
            <div style={{ padding: 14, color: '#64748B' }}>Computing schedule…</div>
          ) : data.length === 0 ? (
            <div style={{ padding: 14, color: '#64748B' }}>No upcoming occurrences with the current configuration.</div>
          ) : (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {data.map((o, i) => {
                const wd = (WEEKDAYS.find((w) => w.v === o.weekday) || {}).l || '';
                return (
                  <li key={i} style={S.scheduleRow}>
                    <span style={{ width: 24, color: '#64748B', fontSize: 11 }}>{i + 1}.</span>
                    <span style={{ width: 110, fontFamily: 'ui-monospace, monospace', color: '#E2E8F0' }}>{o.date}</span>
                    <span style={{ width: 50, color: '#94A3B8', fontSize: 12 }}>{wd}</span>
                    {o.was_preponed && (
                      <span style={S.preponedTag}>
                        preponed from {o.original_date} ({o.reason === 'holiday' ? 'holiday' : 'weekly off'})
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={S.fieldLabel}>{label}</div>
      {children}
    </div>
  );
}

/**
 * Modern chip-style multi-person picker. Selected people render as removable
 * chips; an inline search input expands a popup of matches that aren't yet
 * selected. Click to add. Replaces the native <select multiple> which is
 * fiddly (Cmd-click on Mac, Ctrl-click on Windows) and dated.
 */
function MultiPersonPicker({ value = [], options = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = value || [];
  const selectedSet = new Set(selected);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !selectedSet.has(o.id))
      .filter((o) => !q || o.label.toLowerCase().includes(q))
      .slice(0, 30);
  }, [options, selectedSet, query]);

  const labelOf = (id) => (options.find((o) => o.id === id) || {}).label || id;

  function add(id) {
    onChange([...selected, id]);
    setQuery('');
  }
  function remove(id) {
    onChange(selected.filter((x) => x !== id));
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          padding: '6px 8px', minHeight: 38,
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${open ? 'rgba(0,224,255,0.55)' : 'rgba(255,255,255,0.10)'}`,
          borderRadius: 6, cursor: 'text',
        }}
      >
        {selected.map((id) => (
          <span key={id} style={S.chipPerson}>
            {labelOf(id)}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(id); }}
              style={S.chipPersonX}
              title="Remove"
            >×</button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length ? '' : 'Add people…'}
          style={{
            flex: 1, minWidth: 120, padding: '4px 4px',
            background: 'transparent', border: 'none', outline: 'none',
            color: '#E2E8F0', fontSize: 13,
          }}
        />
      </div>

      {open && (
        <div style={S.pickerPopup}>
          {matches.length === 0 ? (
            <div style={{ padding: 12, fontSize: 12, color: '#64748B' }}>
              {query ? 'No matches.' : 'All available people are already selected.'}
            </div>
          ) : (
            matches.map((m) => (
              <div key={m.id} onClick={() => add(m.id)} style={S.pickerOption}>
                {m.label}
              </div>
            ))
          )}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
        {selected.length} selected
      </div>
    </div>
  );
}

const S = {
  page: { padding: 16, maxWidth: 1400, margin: '0 auto' },
  tabs: { display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tab: {
    padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#94A3B8',
    background: 'transparent', border: 'none', cursor: 'pointer',
    borderBottom: '2px solid transparent',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  tabActive: {
    color: '#00E0FF', borderBottom: '2px solid #00E0FF',
  },
  tabBadge: {
    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
    background: '#F59E0B', color: '#0F172A', minWidth: 18, textAlign: 'center',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  h2: { margin: 0, fontSize: 20, fontWeight: 700, color: '#E2E8F0' },
  primaryBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#fff',
    background: 'linear-gradient(135deg, #FF00E5, #BF5FFF)',
    border: 'none', borderRadius: 8, cursor: 'pointer',
  },
  // Cyan accent button — used for "+ New Reminder" so it stands apart from
  // the magenta Save action and matches the Self Service section accent.
  // Same padding / font size as primaryBtn so they feel like the same family.
  cyanBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#fff',
    background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)',
    border: 'none', borderRadius: 8, cursor: 'pointer',
  },
  ghostBtn: {
    padding: '7px 12px', fontSize: 12, color: '#CBD5E1',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 6, cursor: 'pointer',
  },
  split: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 14, alignItems: 'start' },

  master: {
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12,
    overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '78vh',
  },
  masterHeader: {
    padding: 10, borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  searchInput: {
    padding: '7px 10px', fontSize: 12.5,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, color: '#E2E8F0',
  },
  filterRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },
  filterSel: {
    padding: '6px 8px', fontSize: 11.5,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, color: '#E2E8F0',
  },
  masterList: { overflowY: 'auto', flex: 1 },
  row: {
    display: 'flex', gap: 10, alignItems: 'center', padding: '12px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer',
  },
  rowSel: { background: 'rgba(0,224,255,0.08)' },
  rowName: { fontSize: 13, fontWeight: 600, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowSub: { fontSize: 11, color: '#64748B' },
  pillPaused: {
    fontSize: 9.5, padding: '1px 6px', borderRadius: 999,
    background: 'rgba(245,158,11,0.18)', color: '#F59E0B', marginLeft: 6,
  },
  empty: { padding: 16, textAlign: 'center', color: '#64748B', fontSize: 12.5 },

  detail: {
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12,
    padding: 14, minHeight: 480,
  },
  detailHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 14, flexWrap: 'wrap',
  },
  detailActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  titleInput: {
    flex: 1, minWidth: 200, padding: '8px 10px', fontSize: 15, fontWeight: 600,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 6, color: '#E2E8F0',
  },
  titleReadOnly: {
    flex: 1, minWidth: 200, padding: '8px 0', fontSize: 16, fontWeight: 700,
    color: '#E2E8F0',
  },
  viewValue: {
    padding: '6px 0', fontSize: 13, color: '#E2E8F0',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },

  // ── ScheduleModal ──
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
  },
  modal: {
    background: '#0F172A', border: '1px solid rgba(0,224,255,0.30)',
    borderRadius: 12, width: '100%', maxWidth: 540,
    maxHeight: '80vh', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  modalBody: { padding: '14px 18px', overflowY: 'auto' },
  scheduleRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13,
  },
  preponedTag: {
    fontSize: 10.5, padding: '2px 8px', borderRadius: 999,
    background: 'rgba(245,158,11,0.18)', color: '#F59E0B',
    border: '1px solid rgba(245,158,11,0.4)',
  },

  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  input: {
    width: '100%', padding: '7px 10px', fontSize: 13,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 6, color: '#E2E8F0', boxSizing: 'border-box',
  },

  chipRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: {
    padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 999,
    background: 'rgba(255,255,255,0.04)', color: '#94A3B8',
    border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
  },
  chipOn: {
    background: 'rgba(59,130,246,0.18)', color: '#93C5FD',
    borderColor: 'rgba(59,130,246,0.5)',
  },

  miniDel: {
    width: 22, height: 22, padding: 0, fontSize: 13,
    border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.10)',
    color: '#FCA5A5', borderRadius: 4, cursor: 'pointer',
  },
  addBtn: {
    padding: '6px 10px', fontSize: 11.5, fontWeight: 600,
    border: '1px dashed rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.10)',
    color: '#86EFAC', borderRadius: 6, cursor: 'pointer',
  },

  // ── TaskChainEditor ──
  taskRow: {
    display: 'flex', gap: 6, alignItems: 'center',
    padding: '6px 8px', background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6,
    flexWrap: 'wrap',
  },

  // ── MultiPersonPicker ──
  chipPerson: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', fontSize: 12, fontWeight: 600,
    background: 'rgba(0,224,255,0.12)', color: '#67E8F9',
    border: '1px solid rgba(0,224,255,0.35)', borderRadius: 999,
  },
  chipPersonX: {
    width: 16, height: 16, padding: 0, marginLeft: 2,
    background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)',
    color: '#FCA5A5', borderRadius: 999, fontSize: 11, lineHeight: 1, cursor: 'pointer',
  },
  pickerPopup: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(0,224,255,0.3)',
    borderRadius: 8, maxHeight: 240, overflowY: 'auto', zIndex: 50,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  pickerOption: {
    padding: '8px 12px', fontSize: 13, color: '#CBD5E1',
    cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
};
